export const BRANDING_POR_DEFECTO = Object.freeze({
  nombre_negocio: 'Casa Alessio',
  nombre_app: 'Casa Alessio POS',
  personalizado: false,
  version: 'default',
  logo_url: null,
  icono_192_url: '/logo192.png',
  icono_512_url: '/logo512.png',
  favicon_url: '/favicon.ico',
  manifest_url: '/api/branding/manifest.webmanifest',
});

export async function cargarBrandingPublico(empresaId = 1) {
  try {
    const id=Number(empresaId)||1;
    const res=await fetch(`/api/branding?empresa_id=${id}`,{cache:'no-store'});
    if (!res.ok) return BRANDING_POR_DEFECTO;
    return { ...BRANDING_POR_DEFECTO, ...(await res.json()) };
  } catch {
    return BRANDING_POR_DEFECTO;
  }
}

function asegurarLink(rel, id) {
  let link = document.getElementById(id);
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = rel;
    document.head.appendChild(link);
  }
  return link;
}

export function aplicarBrandingDocumento(branding = BRANDING_POR_DEFECTO) {
  if (typeof document === 'undefined') return;
  const actual = { ...BRANDING_POR_DEFECTO, ...branding };
  document.title = actual.nombre_app || actual.nombre_negocio || 'Casa Alessio POS';

  const icon = asegurarLink('icon', 'branding-favicon');
  icon.type = actual.favicon_url?.endsWith('.ico') ? 'image/x-icon' : 'image/png';
  icon.href = actual.favicon_url || '/favicon.ico';

  const apple = asegurarLink('apple-touch-icon', 'branding-apple-icon');
  apple.href = actual.icono_192_url || '/logo192.png';

  const manifest = asegurarLink('manifest', 'branding-manifest');
  manifest.href = actual.manifest_url || '/api/branding/manifest.webmanifest';

  let meta = document.querySelector('meta[name="application-name"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'application-name';
    document.head.appendChild(meta);
  }
  meta.content = actual.nombre_app || 'Casa Alessio POS';

  let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!appleTitle) {
    appleTitle = document.createElement('meta');
    appleTitle.name = 'apple-mobile-web-app-title';
    document.head.appendChild(appleTitle);
  }
  appleTitle.content = actual.nombre_negocio || 'Casa Alessio';
}

function cargarImagen(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen.'));
    };
    image.src = url;
  });
}

function canvasBlob(canvas, type = 'image/png', quality = 0.95) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('No se pudo procesar la imagen.')),
      type,
      quality,
    );
  });
}

function bytesTotales(archivos) {
  return archivos.reduce((total, archivo) => total + (archivo?.size || 0), 0);
}

function dibujarAjustado(image, width, height, { fondo = null, margen = 0.08 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (fondo) {
    ctx.fillStyle = fondo;
    ctx.fillRect(0, 0, width, height);
  } else {
    ctx.clearRect(0, 0, width, height);
  }
  const maxW = width * (1 - margen * 2);
  const maxH = height * (1 - margen * 2);
  const scale = Math.min(maxW / image.naturalWidth, maxH / image.naturalHeight);
  const drawW = Math.max(1, image.naturalWidth * scale);
  const drawH = Math.max(1, image.naturalHeight * scale);
  ctx.drawImage(image, (width - drawW) / 2, (height - drawH) / 2, drawW, drawH);
  return canvas;
}

export async function prepararArchivosBranding(file) {
  if (!file) throw new Error('Seleccioná una imagen.');
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Usá una imagen PNG, JPG o WebP.');
  }
  if (file.size > 2 * 1024 * 1024) throw new Error('La imagen no puede superar 2 MB.');

  const image = await cargarImagen(file);
  if (!image.naturalWidth || !image.naturalHeight) throw new Error('La imagen no tiene dimensiones válidas.');
  if (image.naturalWidth > 12000 || image.naturalHeight > 12000 || image.naturalWidth * image.naturalHeight > 40_000_000) {
    throw new Error('La imagen tiene dimensiones demasiado grandes. Usá una de hasta 12.000 px por lado.');
  }

  const crearLogoCanvas = (maxW, maxH, fondo = null) => {
    const scale = Math.min(1, maxW / image.naturalWidth, maxH / image.naturalHeight);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    return dibujarAjustado(image, width, height, { margen: 0, fondo });
  };

  const icono192Canvas = dibujarAjustado(image, 192, 192, { fondo: '#ffffff', margen: 0.1 });
  const icono512Canvas = dibujarAjustado(image, 512, 512, { fondo: '#ffffff', margen: 0.1 });
  const faviconCanvas = dibujarAjustado(image, 64, 64, { fondo: '#ffffff', margen: 0.08 });

  let [logo, icono192, icono512, favicon] = await Promise.all([
    canvasBlob(crearLogoCanvas(1200, 600)),
    canvasBlob(icono192Canvas),
    canvasBlob(icono512Canvas),
    canvasBlob(faviconCanvas),
  ]);

  // El Nginx público suele aceptar 1 MB por solicitud. Se comprime de forma
  // adaptativa para que el conjunto completo pueda subirse sin tocar el proxy externo.
  if (bytesTotales([logo, icono192, icono512, favicon]) > 850 * 1024) {
    [logo, icono512] = await Promise.all([
      canvasBlob(crearLogoCanvas(1000, 500, '#ffffff'), 'image/jpeg', 0.84),
      canvasBlob(icono512Canvas, 'image/jpeg', 0.82),
    ]);
  }

  if (bytesTotales([logo, icono192, icono512, favicon]) > 900 * 1024) {
    [logo, icono512] = await Promise.all([
      canvasBlob(crearLogoCanvas(700, 350, '#ffffff'), 'image/jpeg', 0.72),
      canvasBlob(icono512Canvas, 'image/jpeg', 0.70),
    ]);
  }

  if (bytesTotales([logo, icono192, icono512, favicon]) > 980 * 1024) {
    throw new Error('No se pudo comprimir el logo lo suficiente. Probá con una imagen más simple o más pequeña.');
  }

  return {
    logo,
    icono192,
    icono512,
    favicon,
    totalBytes: bytesTotales([logo, icono192, icono512, favicon]),
    previewUrl: URL.createObjectURL(logo),
  };
}

export function htmlLogoBranding(config, { maxWidth = 150, maxHeight = 70 } = {}) {
  const url = config?.logo_url;
  if (!url) return '';
  return `<img src="${String(url).replace(/"/g, '&quot;')}" alt="Logo" style="display:block;max-width:${maxWidth}px;max-height:${maxHeight}px;object-fit:contain;margin:0 auto 5px;"/>`;
}
